/**
 * Controle de pendente revistoria.
 *
 * Quem passou de 5 dias em atraso e pagou vai para PENDENTE REVISTORIA no SGA:
 * voltou a pagar, mas só retoma a proteção depois de nova vistoria. A tela
 * responde três perguntas:
 *
 *  · quem está parado, e há quanto tempo — o que trava a volta à proteção;
 *  · o que o processo deixou passar — pagou atrasado e não foi para revistoria,
 *    ou está em revistoria sem pagamento atrasado que explique;
 *  · se a fila anda — entradas e saídas por mês, e o tempo médio até sair.
 *
 * A placa sai da lista sozinha quando o SGA muda a situação.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight,
  ClipboardCheck,
  Copy,
  FileDown,
  FileQuestion,
  Hourglass,
  Printer,
  Receipt,
  ShieldX,
} from 'lucide-react';
import { api } from '../../lib/api.ts';
import { formatarCpf, moeda, numero } from '../../lib/format.ts';
import { exportarPdf, exportarPlanilha, exportarTexto } from '../../lib/exportar.ts';
import {
  Aviso,
  Botao,
  Card,
  Esqueleto,
  EstadoVazio,
  Segmentado,
  TituloSecao,
} from '../../components/ui/index.tsx';
import Folha from '../../components/ui/Folha.tsx';
import { SeletorOrdem, ThOrdenavel, useOrdenacao } from '../../components/ui/Ordenacao.tsx';
import {
  BarraComposicao,
  CartaoDado,
  GradeBento,
  MiniDados,
} from '../../components/ui/Bento.tsx';
import BarraFiltros, {
  filtroInicial,
  type OpcoesFiltro,
  type ValorFiltro,
} from '../../components/filtros/BarraFiltros.tsx';
import {
  AbasDetalhe,
  BotoesContato,
  Dado,
  DadosAssociado,
  DadosVeiculo,
  dataBr,
  Etiquetas,
  GradeDados,
  LinhaFaixa,
  mesCurto,
  type Associado,
  type TomEtiqueta,
} from './cobranca.tsx';

type FaixaTempo = 'ate7' | 'ate15' | 'ate30' | 'mais30';

interface Base {
  placa: string | null;
  associado: Associado;
  veiculo: { marca: string | null; modelo: string | null; situacao: string | null };
  cooperativa: string | null;
  consultor: string | null;
  pagamentoAtrasado: { vencimento: string; pagamento: string; dias: number; valor: number } | null;
  emAberto: { titulos: number; valor: number; dias: number } | null;
}

interface EmRevistoria extends Base {
  desde: string | null;
  dias: number | null;
  diasMinimo: boolean;
  semPagamentoAtrasado: boolean;
}

interface Resposta {
  historicoDesde: string | null;
  resumo: {
    emRevistoria: number;
    mediaDias: number | null;
    comBoletoEmAberto: number;
    valorEmAberto: number;
    semPagamentoAtrasado: number;
    pagouSemRevistoria: number;
  };
  faixas: { id: FaixaTempo; rotulo: string; placas: number }[];
  saidas: { dias: number; liberadas: number; inativadas: number; mediaDias: number | null };
  fluxo: { mes: string; entradas: number; liberadas: number; inativadas: number }[];
  itens: EmRevistoria[];
  divergentes: Base[];
}

type Lista = 'revistoria' | 'divergentes';

type Recorte =
  | { tipo: 'todos' }
  | { tipo: 'faixa'; faixa: FaixaTempo; rotulo: string }
  | { tipo: 'em_aberto' }
  | { tipo: 'sem_pagamento' };

const TODOS: Recorte = { tipo: 'todos' };

const COR_FAIXA: Record<FaixaTempo, string> = {
  ate7: 'var(--chart-2)',
  ate15: 'var(--warn)',
  ate30: 'color-mix(in oklab, var(--warn) 50%, var(--danger))',
  mais30: 'var(--danger)',
};

const LIMITES: Record<FaixaTempo, [number, number]> = {
  ate7: [0, 7],
  ate15: [8, 15],
  ate30: [16, 30],
  mais30: [31, Infinity],
};

const diasTexto = (i: EmRevistoria) =>
  i.dias === null ? '—' : `${i.diasMinimo ? '+' : ''}${i.dias}d`;

function marcas(i: Base & Partial<EmRevistoria>) {
  const m: { texto: string; tom: TomEtiqueta }[] = [];
  if (i.emAberto) m.push({ texto: 'boleto em aberto', tom: 'perigo' });
  if (i.semPagamentoAtrasado) m.push({ texto: 'sem pagamento atrasado', tom: 'alerta' });
  return m;
}

function descrever(r: Recorte): string {
  switch (r.tipo) {
    case 'faixa':
      return `há ${r.rotulo}`;
    case 'em_aberto':
      return 'com boleto em aberto';
    case 'sem_pagamento':
      return 'sem pagamento atrasado';
    default:
      return '';
  }
}

export default function Revistoria() {
  const [filtro, setFiltro] = useState<ValorFiltro>(filtroInicial);
  const [lista, setLista] = useState<Lista>('revistoria');
  const [recorte, setRecorte] = useState<Recorte>(TODOS);
  const [aviso, setAviso] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (filtro.cooperativa) params.set('cooperativa', filtro.cooperativa);
  if (filtro.consultor) params.set('consultor', filtro.consultor);
  const qs = params.toString();

  const { data: opcoes } = useQuery({
    queryKey: ['vendas', 'filtros'],
    queryFn: () => api.get<OpcoesFiltro>('/vendas/filtros'),
    staleTime: 10 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['revistoria', qs],
    queryFn: () => api.get<Resposta>(`/revistoria?${qs}`),
  });

  const escolher = useCallback((l: Lista, r: Recorte = TODOS) => {
    setLista(l);
    setRecorte((atual) => (JSON.stringify(atual) === JSON.stringify(r) ? TODOS : r));
  }, []);

  const itens = useMemo(() => {
    const todos = data?.itens ?? [];
    switch (recorte.tipo) {
      case 'faixa': {
        const [de, ate] = LIMITES[recorte.faixa];
        return todos.filter((i) => (i.dias ?? 0) >= de && (i.dias ?? 0) <= ate);
      }
      case 'em_aberto':
        return todos.filter((i) => i.emAberto);
      case 'sem_pagamento':
        return todos.filter((i) => i.semPagamentoAtrasado);
      default:
        return todos;
    }
  }, [data, recorte]);

  const linhas: Base[] = lista === 'revistoria' ? itens : (data?.divergentes ?? []);
  const titulo = lista === 'revistoria' ? 'Pendente revistoria' : 'Pagou atrasado e não foi para revistoria';

  async function copiarTexto() {
    const corpo = linhas.map((i) => {
      const r = i as Partial<EmRevistoria>;
      const pa = i.pagamentoAtrasado;
      return (
        `• *${i.associado.nome ?? 'Sem nome'}* — ${i.placa ?? 's/ placa'}\n` +
        (r.dias !== undefined && r.dias !== null
          ? `  em revistoria há ${r.diasMinimo ? 'mais de ' : ''}${r.dias} dias\n`
          : '') +
        (pa ? `  pagou ${dataBr(pa.pagamento)} com ${pa.dias} dias de atraso\n` : '') +
        `  ${i.associado.celular ?? i.associado.telefone ?? 'sem telefone'}`
      );
    });
    const cabecalho =
      `*${titulo}*${recorte.tipo !== 'todos' && lista === 'revistoria' ? ` · ${descrever(recorte)}` : ''} — ` +
      `${linhas.length} placa(s)\n_Gerado em ${new Date().toLocaleString('pt-BR')}_\n\n`;
    const r = await exportarTexto(cabecalho + corpo.join('\n\n'), titulo);
    setAviso(
      r.compartilhado ? null : r.ok ? 'Lista copiada — é só colar no WhatsApp.' : 'Não consegui copiar.',
    );
  }

  function baixarPlanilha() {
    exportarPlanilha(
      [
        { chave: 'associado', titulo: 'Associado' },
        { chave: 'cpf', titulo: 'CPF' },
        { chave: 'celular', titulo: 'Celular' },
        { chave: 'placa', titulo: 'Placa' },
        { chave: 'veiculo', titulo: 'Veículo' },
        { chave: 'situacao', titulo: 'Situação' },
        { chave: 'desde', titulo: 'Em revistoria desde' },
        { chave: 'dias', titulo: 'Dias em revistoria' },
        { chave: 'vencimento', titulo: 'Vencimento do boleto pago atrasado' },
        { chave: 'pagamento', titulo: 'Pago em' },
        { chave: 'atraso', titulo: 'Dias de atraso no pagamento' },
        { chave: 'aberto', titulo: 'Em aberto hoje' },
        { chave: 'cooperativa', titulo: 'Cooperativa' },
        { chave: 'consultor', titulo: 'Consultor' },
      ],
      linhas.map((i) => {
        const r = i as Partial<EmRevistoria>;
        return {
          associado: i.associado.nome,
          cpf: formatarCpf(i.associado.cpf),
          celular: i.associado.celular ?? i.associado.telefone,
          placa: i.placa,
          veiculo: [i.veiculo.marca, i.veiculo.modelo].filter(Boolean).join(' '),
          situacao: i.veiculo.situacao,
          desde: r.desde ? dataBr(r.desde) : r.diasMinimo ? 'antes do histórico' : null,
          dias: r.dias ?? null,
          vencimento: dataBr(i.pagamentoAtrasado?.vencimento),
          pagamento: dataBr(i.pagamentoAtrasado?.pagamento),
          atraso: i.pagamentoAtrasado?.dias ?? null,
          aberto: i.emAberto ? i.emAberto.valor.toFixed(2).replace('.', ',') : null,
          cooperativa: i.cooperativa,
          consultor: i.consultor,
        };
      }),
      lista === 'revistoria' ? 'pendente-revistoria' : 'pagou-sem-revistoria',
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Esqueleto key={i} className="h-28" />
          ))}
        </div>
        <Esqueleto className="h-64" />
      </div>
    );
  }

  const r = data.resumo;
  const maxFaixa = Math.max(...data.faixas.map((f) => f.placas), 1);

  return (
    <div className="flex flex-col gap-4">
      <div className="imprimir-oculto">
        <BarraFiltros valor={filtro} onChange={setFiltro} opcoes={opcoes} semPeriodo />
      </div>

      <div className="hidden imprimir-visivel">
        <h1 className="text-lg font-semibold">{titulo}</h1>
        <p className="text-[13px] text-[var(--muted)]">
          {linhas.length} placas · {new Date().toLocaleString('pt-BR')}
        </p>
      </div>

      <GradeBento>
        <CartaoDado
          tamanho="destaque"
          rotulo="Em revistoria"
          valor={numero(r.emRevistoria)}
          nota={
            r.mediaDias === null
              ? 'placas aguardando vistoria'
              : `placas aguardando vistoria · em média há ${Math.round(r.mediaDias)} dias`
          }
          Icone={ClipboardCheck}
          tom="marca"
        >
          <BarraComposicao
            fatias={data.faixas.map((f) => ({
              rotulo: f.rotulo,
              valor: f.placas,
              cor: COR_FAIXA[f.id],
            }))}
          />
          <div className="mt-4">
            <MiniDados
              itens={[
                {
                  rotulo: 'Há mais de 30 dias',
                  valor: numero(data.faixas.find((f) => f.id === 'mais30')?.placas ?? 0),
                  tom: 'perigo',
                },
                {
                  rotulo: `Entraram · ${data.fluxo.length ? mesCurto(data.fluxo.at(-1)!.mes) : '—'}`,
                  valor: numero(data.fluxo.at(-1)?.entradas ?? 0),
                },
                {
                  rotulo: `Liberadas · ${data.fluxo.length ? mesCurto(data.fluxo.at(-1)!.mes) : '—'}`,
                  valor: numero(data.fluxo.at(-1)?.liberadas ?? 0),
                  tom: 'sucesso',
                },
              ]}
            />
          </div>
        </CartaoDado>
        <CartaoDado
          rotulo="Ainda com boleto em aberto"
          valor={numero(r.comBoletoEmAberto)}
          nota={`${moeda(r.valorEmAberto)} vencido — pagou um, deve outro`}
          Icone={Receipt}
          tom="perigo"
          ativo={lista === 'revistoria' && recorte.tipo === 'em_aberto'}
          onClick={() => escolher('revistoria', { tipo: 'em_aberto' })}
        />
        <CartaoDado
          rotulo="Pagou e não foi para revistoria"
          valor={numero(r.pagouSemRevistoria)}
          nota="pagou com +5 dias e seguiu protegido"
          Icone={ShieldX}
          tom="perigo"
          ativo={lista === 'divergentes'}
          onClick={() => escolher('divergentes')}
        />
        <CartaoDado
          rotulo="Em revistoria sem pagamento atrasado"
          valor={numero(r.semPagamentoAtrasado)}
          nota="nenhum boleto pago com +5 dias que explique"
          Icone={FileQuestion}
          tom="alerta"
          ativo={lista === 'revistoria' && recorte.tipo === 'sem_pagamento'}
          onClick={() => escolher('revistoria', { tipo: 'sem_pagamento' })}
        />
        <CartaoDado
          rotulo={`Saíram em ${data.saidas.dias} dias`}
          valor={numero(data.saidas.liberadas + data.saidas.inativadas)}
          nota={
            `${numero(data.saidas.liberadas)} liberadas · ${numero(data.saidas.inativadas)} inativadas` +
            (data.saidas.mediaDias === null
              ? ''
              : ` · ${Math.round(data.saidas.mediaDias)} dias em média`)
          }
          Icone={Hourglass}
        />
      </GradeBento>

      {data.historicoDesde && (
        <Aviso>
          O tempo em revistoria é contado pelo histórico de situações, que começa em{' '}
          {dataBr(data.historicoDesde)}. Quem já estava em revistoria antes disso aparece com “+”:
          é o mínimo, não o tempo exato.
        </Aviso>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <TituloSecao>Tempo em revistoria</TituloSecao>
          <div className="-mx-2 flex flex-col gap-1">
            {data.faixas.map((f) => (
              <LinhaFaixa
                key={f.id}
                rotulo={f.rotulo}
                placas={f.placas}
                fracao={f.placas / maxFaixa}
                cor={COR_FAIXA[f.id]}
                ativo={recorte.tipo === 'faixa' && recorte.faixa === f.id}
                onClick={() => escolher('revistoria', { tipo: 'faixa', faixa: f.id, rotulo: f.rotulo })}
              />
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <TituloSecao>Entradas e saídas por mês</TituloSecao>
          {data.fluxo.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--muted)]">Sem movimento registrado.</p>
          ) : (
            <TabelaFluxo fluxo={data.fluxo} />
          )}
          <p className="mt-3 text-xs text-[var(--faint)]">
            <strong>Liberadas</strong>: voltaram a ATIVO depois da vistoria.{' '}
            <strong>Inativadas</strong>: saíram da revistoria para fora da carteira.
          </p>
        </Card>
      </div>

      <div className="imprimir-oculto flex flex-col gap-3">
        <div className="scroll-x">
          <Segmentado
            rotulo="Lista"
            valor={lista}
            onChange={(l) => escolher(l)}
            opcoes={[
              { valor: 'revistoria', rotulo: `Em revistoria (${numero(r.emRevistoria)})` },
              {
                valor: 'divergentes',
                rotulo: `Pagou e não foi (${numero(r.pagouSemRevistoria)})`,
              },
            ]}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Botao tamanho="sm" icone={<Copy size={14} />} onClick={() => void copiarTexto()}>
            Texto / WhatsApp
          </Botao>
          <Botao tamanho="sm" icone={<FileDown size={14} />} onClick={baixarPlanilha}>
            Planilha
          </Botao>
          <Botao tamanho="sm" icone={<Printer size={14} />} onClick={exportarPdf}>
            PDF
          </Botao>
          {recorte.tipo !== 'todos' && lista === 'revistoria' && (
            <Botao tamanho="sm" variante="fantasma" onClick={() => setRecorte(TODOS)}>
              Limpar recorte · {descrever(recorte)}
            </Botao>
          )}
          <span className="ml-auto text-[13px] text-[var(--muted)]">
            {numero(linhas.length)} placas
          </span>
        </div>
      </div>

      {lista === 'divergentes' && (
        <Aviso>
          Pagou um boleto com mais de 5 dias de atraso, está quitado e o veículo não passou por
          PENDENTE REVISTORIA desde aquele vencimento. Conferir no SGA se a vistoria foi
          dispensada ou se o processo não rodou.
        </Aviso>
      )}

      {aviso && <Aviso>{aviso}</Aviso>}

      {linhas.length === 0 ? (
        <Card className="p-2">
          <EstadoVazio
            icone={<ClipboardCheck size={20} />}
            titulo="Nenhuma placa neste recorte"
            descricao="Ou a fila está em dia, ou os filtros acima estão restritos demais."
          />
        </Card>
      ) : (
        <Tabela key={lista} itens={linhas} emRevistoria={lista === 'revistoria'} />
      )}
    </div>
  );
}

function TabelaFluxo({ fluxo }: { fluxo: Resposta['fluxo'] }) {
  const { ordenados, ordem, alternar } = useOrdenacao(
    fluxo,
    {
      mes: (m) => m.mes,
      entradas: (m) => m.entradas,
      liberadas: (m) => m.liberadas,
      inativadas: (m) => m.inativadas,
    },
    { coluna: 'mes', desc: true },
  );
  const th = { ordem, onOrdenar: alternar };
  return (
    // Margem negativa: o botão do título tem o próprio respiro, e a coluna
    // continua alinhada com o texto do cartão.
    <table className="-mx-3 w-[calc(100%+24px)] table-fixed text-[13px]">
      <thead className="text-[var(--muted)]">
        <tr className="border-b border-[var(--border)]">
          <ThOrdenavel coluna="mes" {...th}>Mês</ThOrdenavel>
          <ThOrdenavel coluna="entradas" direita {...th}>Entraram</ThOrdenavel>
          <ThOrdenavel coluna="liberadas" direita {...th}>Liberadas</ThOrdenavel>
          <ThOrdenavel coluna="inativadas" direita {...th}>Inativadas</ThOrdenavel>
        </tr>
      </thead>
      <tbody>
        {ordenados.map((m) => (
          <tr key={m.mes} className="border-b border-[var(--border)] last:border-0">
            <td className="px-3 py-2 font-medium text-[var(--fg)]">{mesCurto(m.mes)}</td>
            <td className="num px-3 py-2 text-right">{numero(m.entradas)}</td>
            <td className="num px-3 py-2 text-right text-[var(--success)]">{numero(m.liberadas)}</td>
            <td className="num px-3 py-2 text-right text-[var(--danger)]">{numero(m.inativadas)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────── Lista ───────────────────────────

/**
 * Tabela a partir de 1024px, cartões abaixo — sem rolagem horizontal. A placa
 * e o contato vão embaixo do nome; consultor só a partir de 1280px.
 */
function Tabela({ itens: brutos, emRevistoria }: { itens: Base[]; emRevistoria: boolean }) {
  const [detalhe, setDetalhe] = useState<Base | null>(null);
  const fechar = useCallback(() => setDetalhe(null), []);
  // Em revistoria começa pelos parados há mais tempo; na divergência, pelo
  // pagamento mais recente — é o caso que ainda dá para corrigir.
  const { ordenados: itens, ordem, alternar, definir } = useOrdenacao(
    brutos,
    {
      associado: (i) => i.associado.nome,
      tempo: (i) =>
        emRevistoria ? ((i as EmRevistoria).dias ?? null) : i.veiculo.situacao,
      pagamento: (i) => i.pagamentoAtrasado?.pagamento ?? null,
      emAberto: (i) => i.emAberto?.valor ?? null,
      consultor: (i) => i.consultor,
    },
    emRevistoria ? { coluna: 'tempo', desc: true } : { coluna: 'pagamento', desc: true },
  );
  const th = { ordem, onOrdenar: alternar };

  return (
    <>
      <Card className="hidden overflow-hidden lg:block">
        <table className="w-full table-fixed text-[13px]">
          <thead className="border-b border-[var(--border)] bg-[var(--bg-subtle)] text-left">
            <tr className="text-[var(--muted)]">
              <ThOrdenavel coluna="associado" {...th}>Associado</ThOrdenavel>
              <ThOrdenavel coluna="tempo" direita={emRevistoria} className="w-32" {...th}>
                {emRevistoria ? 'Em revistoria' : 'Situação'}
              </ThOrdenavel>
              <ThOrdenavel coluna="pagamento" className="w-40" {...th}>
                Pagamento atrasado
              </ThOrdenavel>
              <ThOrdenavel coluna="emAberto" direita className="w-32" {...th}>Em aberto</ThOrdenavel>
              <ThOrdenavel coluna="consultor" className="hidden w-48 xl:table-cell" {...th}>
                Consultor
              </ThOrdenavel>
              <th className="w-10 p-3">
                <span className="sr-only">Detalhes</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i) => {
              const r = i as EmRevistoria;
              return (
                <tr
                  key={`${i.placa}-${i.associado.codigo}`}
                  onClick={() => setDetalhe(i)}
                  className="group cursor-pointer border-b border-[var(--border)] transition-colors last:border-0 hover:bg-[var(--surface-hover)]"
                >
                  <td className="p-3">
                    <p
                      className="truncate font-medium text-[var(--fg)]"
                      title={i.associado.nome ?? undefined}
                    >
                      {i.associado.nome ?? '—'}
                    </p>
                    <p className="num truncate text-xs text-[var(--faint)]">
                      <span className="text-[var(--muted)]">{i.placa ?? 's/ placa'}</span>
                      {(i.associado.celular ?? i.associado.telefone) &&
                        ` · ${i.associado.celular ?? i.associado.telefone}`}
                    </p>
                    <Etiquetas itens={marcas(i)} />
                  </td>
                  {emRevistoria ? (
                    <td className="num p-3 text-right text-[var(--fg)]">
                      {diasTexto(r)}
                      <span className="block text-[11px] text-[var(--faint)]">
                        {r.desde ? `desde ${dataBr(r.desde)}` : 'antes do histórico'}
                      </span>
                    </td>
                  ) : (
                    <td className="truncate p-3 text-[var(--muted)]">{i.veiculo.situacao ?? '—'}</td>
                  )}
                  <td className="num p-3 text-[var(--muted)]">
                    {i.pagamentoAtrasado ? (
                      <>
                        pago {dataBr(i.pagamentoAtrasado.pagamento)}
                        <span className="block text-[11px] text-[var(--faint)]">
                          {i.pagamentoAtrasado.dias} dias de atraso
                        </span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num p-3 text-right">
                    {i.emAberto ? (
                      <span className="font-medium text-[var(--danger)]">
                        {moeda(i.emAberto.valor)}
                        <span className="block text-[11px] font-normal text-[var(--faint)]">
                          {i.emAberto.dias}d · {i.emAberto.titulos} tít.
                        </span>
                      </span>
                    ) : (
                      <span className="text-[var(--faint)]">—</span>
                    )}
                  </td>
                  <td className="hidden p-3 text-[var(--muted)] xl:table-cell">
                    <span className="block truncate" title={i.consultor ?? undefined}>
                      {i.consultor ?? '—'}
                    </span>
                  </td>
                  <td className="p-3">
                    <ChevronRight
                      size={16}
                      aria-hidden
                      className="text-[var(--faint)] group-hover:text-[var(--fg)]"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <SeletorOrdem
        className="lg:hidden"
        ordem={ordem}
        onChange={definir}
        opcoes={[
          { coluna: 'tempo', rotulo: emRevistoria ? 'Tempo em revistoria' : 'Situação' },
          { coluna: 'pagamento', rotulo: 'Data do pagamento' },
          { coluna: 'emAberto', rotulo: 'Valor em aberto' },
          { coluna: 'associado', rotulo: 'Associado' },
          { coluna: 'consultor', rotulo: 'Consultor' },
        ]}
      />
      <ul className="grid list-none gap-3 p-0 md:grid-cols-2 lg:hidden">
        {itens.map((i) => {
          const r = i as EmRevistoria;
          return (
            <Card as="li" key={`${i.placa}-${i.associado.codigo}`} className="p-4">
              <button
                onClick={() => setDetalhe(i)}
                aria-haspopup="dialog"
                className="anel-foco -m-1 flex w-[calc(100%+8px)] items-start justify-between gap-3 rounded-[var(--r-md)] p-1 text-left"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-[var(--fg)]">
                    {i.associado.nome ?? '—'}
                  </p>
                  <p className="num text-xs text-[var(--faint)]">
                    {i.placa ?? '—'} · {formatarCpf(i.associado.cpf)}
                  </p>
                </div>
                <div className="flex shrink-0 items-start gap-1.5 text-right">
                  <div>
                    {emRevistoria ? (
                      <p className="num font-semibold text-[var(--fg)]">{diasTexto(r)}</p>
                    ) : (
                      <p className="text-xs text-[var(--muted)]">{i.veiculo.situacao}</p>
                    )}
                    {i.pagamentoAtrasado && (
                      <p className="num text-xs text-[var(--faint)]">
                        pagou c/ {i.pagamentoAtrasado.dias}d
                      </p>
                    )}
                  </div>
                  <ChevronRight size={18} aria-hidden className="mt-0.5 text-[var(--faint)]" />
                </div>
              </button>
              <Etiquetas itens={marcas(i)} />
              <BotoesContato associado={i.associado} compacto />
            </Card>
          );
        })}
      </ul>

      <Folha
        aberta={detalhe !== null}
        larga
        titulo={detalhe?.associado.nome ?? detalhe?.placa ?? ''}
        onFechar={fechar}
      >
        {detalhe && <Detalhe item={detalhe} />}
      </Folha>
    </>
  );
}

function Detalhe({ item: i }: { item: Base }) {
  const r = i as Partial<EmRevistoria>;
  const pa = i.pagamentoAtrasado;

  return (
    <div className="flex flex-col gap-5 p-1 text-[13px]">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          {r.dias !== undefined ? (
            <>
              <p className="num text-2xl font-semibold text-[var(--fg)]">
                {r.dias === null ? '—' : `${r.diasMinimo ? '+' : ''}${r.dias} dias`}
              </p>
              <p className="text-[var(--muted)]">
                em revistoria
                {r.desde ? ` desde ${dataBr(r.desde)}` : ' (desde antes do histórico)'} ·{' '}
                <span className="num">{i.placa}</span>
              </p>
            </>
          ) : (
            <p className="text-[var(--muted)]">
              <span className="num text-[var(--fg)]">{i.placa}</span> · {i.veiculo.situacao}
            </p>
          )}
        </div>
        <Etiquetas itens={marcas(i)} />
      </div>
      <BotoesContato associado={i.associado} />

      <AbasDetalhe
        abas={[
          {
            id: 'revistoria',
            rotulo: 'Revistoria',
            conteudo: (
              <GradeDados>
                <Dado
                  rotulo="Em revistoria desde"
                  valor={r.desde ? dataBr(r.desde) : r.diasMinimo ? 'antes do histórico' : null}
                  num
                />
                <Dado
                  rotulo="Tempo parado"
                  valor={
                    r.dias === undefined || r.dias === null
                      ? null
                      : `${r.diasMinimo ? 'pelo menos ' : ''}${r.dias} dias`
                  }
                />
                <Dado rotulo="Boleto pago atrasado — vencimento" valor={dataBr(pa?.vencimento)} num />
                <Dado rotulo="Pago em" valor={dataBr(pa?.pagamento)} num />
                <Dado rotulo="Atraso no pagamento" valor={pa ? `${pa.dias} dias` : null} num />
                <Dado rotulo="Valor pago" valor={pa ? moeda(pa.valor) : null} num />
                <Dado
                  rotulo="Em aberto hoje"
                  valor={
                    i.emAberto
                      ? `${moeda(i.emAberto.valor)} · ${i.emAberto.titulos} tít. · ${i.emAberto.dias} dias`
                      : 'nada vencido'
                  }
                  num
                  largo
                />
              </GradeDados>
            ),
          },
          { id: 'associado', rotulo: 'Associado', conteudo: <DadosAssociado associado={i.associado} /> },
          {
            id: 'veiculo',
            rotulo: 'Veículo',
            conteudo: (
              <DadosVeiculo
                placa={i.placa}
                veiculo={i.veiculo}
                cooperativa={i.cooperativa}
                consultor={i.consultor}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
